import type { ButtonHTMLAttributes } from 'react';
import { ImSpinner8 } from 'react-icons/im';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: keyof typeof SIZE;
  variant?: keyof typeof VARIANT | 'none';
  color?: keyof typeof COLOR.outlined;
  isLoading?: boolean;
}

const SIZE = {
  xs: 'h-6 px-4 rounded-md text-sm',
  sm: `h-8 px-4 rounded-lg text-sm`,
  md: `h-12 px-4 rounded-[10px]`,
  lg: `block mx-auto max-w-lg w-full h-[52px] px-4 rounded-[10px]`,
} as const;

const VARIANT = {
  contained:
    'disabled:bg-gray-300 disabled:text-white disabled:fill-white pointer-hover:disabled:hover:bg-gray-300 pointer-hover:disabled:hover:text-white pointer-hover:disabled:hover:fill-white',
  outlined:
    'bg-white outline outline-1 -outline-offset-1 pointer-hover:hover:outline-2 pointer-hover:hover:-outline-offset-2 focus:outline-2 focus:-outline-offset-2 active:outline-2 active:-outline-offset-2 disabled:bg-gray-0 disabled:outline-gray-300 disabled:text-gray-300 disabled:fill-gray-300',
} as const;

const COLOR = {
  contained: {
    primary: 'bg-purple-600 text-white fill-white pointer-hover:hover:bg-purple-600 focus:bg-purple-600 active:bg-purple-700',
    navy: 'bg-navy-500 text-white fill-white pointer-hover:hover:bg-navy-600 focus:bg-navy-600 active:bg-navy-700',
    gray: 'bg-gray-200 text-gray-800 fill-gray-800 pointer-hover:hover:bg-gray-300 focus:bg-gray-300 active:bg-gray-400',
    red: '',
  },
  outlined: {
    primary:
      'outline-purple-600 text-purple-600 fill-purple-600 pointer-hover:hover:outline-purple-600 pointer-hover:hover:text-purple-600 pointer-hover:hover:fill-purple-600 focus:outline-purple-600 focus:text-purple-600 focus:fill-purple-600 active:bg-purple-50 active:outline-purple-700 active:text-purple-700 active:fill-purple-700',
    navy: 'outline-navy-500 text-navy-500 fill-navy-500 pointer-hover:hover:outline-navy-600 pointer-hover:hover:text-navy-600 pointer-hover:hover:fill-navy-600 focus:outline-navy-600 focus:text-navy-600 focus:fill-navy-600 active:bg-navy-50 active:outline-navy-700 active:text-navy-700 active:fill-navy-700',
    gray: 'outline-gray-200 text-gray-800 fill-gray-800 pointer-hover:hover:outline-gray-400 focus:outline-gray-400 active:bg-gray-50 active:outline-gray-400',
    red: 'outline-red-800 text-red-800 fill-red-800 pointer-hover:hover:outline-red-900 pointer-hover:hover:text-red-900 pointer-hover:hover:fill-red-900 focus:outline-red-900 focus:text-red-900 focus:fill-red-900 active:bg-red-100 active:outline-red-1000 active:text-red-1000 active:fill-red-1000',
  },
} as const;

const Button = ({
  size = 'lg',
  color = 'primary',
  variant = 'contained',
  type = 'button',
  isLoading,
  className,
  children,
  onClick,
  ...props
}: ButtonProps) => {
  const sizeStyle = SIZE[size];
  const colorStyle = variant === 'none' ? '' : `${VARIANT[variant]} ${COLOR[variant][color]}`;

  return (
    <button
      type={type}
      className={`flex items-center justify-center font-semibold ${sizeStyle} ${colorStyle}` + (className ? ` ${className}` : '')}
      onClick={onClick}
      {...props}
    >
      {isLoading ? <ImSpinner8 className="animate-spin mx-auto fill-white" /> : children}
    </button>
  );
};

export default Button;
